import Dexie from 'dexie';

import db from './db';

// Sentinel value used to build a Dexie Collection that matches zero rows. Callers
// chain .count()/.primaryKeys()/.first()/.delete() on these queries, so when a
// required arg is missing we still have to return a real Collection — querying
// for a value no real row will ever hold gives us one. Must be an IndexedDB-
// valid key (string/number/Date/array/binary); Symbols would throw DataError.
const NO_MATCH = '__missing__';

const lastArg = (args) => args[args.length - 1];

// Grab a single message by id, used when we have an id in hand (replies, modals, etc).
export const messageById = (...args) => {
  const messageId = lastArg(args);
  return messageId ? db.messages.get(String(messageId)) : Promise.resolve(null);
};

// Bulk lookup for a batch of ids, lets callers hydrate a list of messages in one hit.
export const messagesByIds = (...args) => {
  const messageIds = lastArg(args) || [];
  if (!Array.isArray(messageIds) || messageIds.length === 0) return Promise.resolve([]);
  return db.messages.bulkGet(messageIds.filter(Boolean).map((id) => String(id)));
};

// Point lookup for a known channel row by sort order.
export const messagesByChannelAndSortOrder = (...args) => {
  const [channelId, sortOrder] = args.length > 2 ? args.slice(-2) : args;
  if (!channelId || !sortOrder) return db.messages.where('[channel_id+sort_order]').equals([NO_MATCH, NO_MATCH]);
  return db.messages.where('[channel_id+sort_order]').equals([String(channelId), sortOrder]);
};

// Workspace-scoped lookup for debug/search flows that know the workspace but not the channel.
export const messagesByWorkspaceAndSortOrder = (...args) => {
  const [workspaceId, sortOrder] = args.length > 2 ? args.slice(-2) : args;
  if (!workspaceId || !sortOrder) return db.messages.where('[workspace_id+sort_order]').equals([NO_MATCH, NO_MATCH]);
  return db.messages.where('[workspace_id+sort_order]').equals([String(workspaceId), sortOrder]);
};

// Skeleton/optimistic rows live in the same table as real messages but tagged by pseudotype. Use this to find or delete them by sort_order.
export const messagesByPseudotypeAndSortOrder = (...args) => {
  const [pseudotype, sortOrder] = args.length > 2 ? args.slice(-2) : args;
  if (!pseudotype || !sortOrder) return db.messages.where('[pseudotype+sort_order]').equals([NO_MATCH, NO_MATCH]);
  return db.messages.where('[pseudotype+sort_order]').equals([pseudotype, sortOrder]);
};

// Every optimistic row across all channels, used by live-journal to sweep/retry pending sends on boot.
export const allOptimisticMessages = () =>
  db.messages.where('[pseudotype+sort_order]').between(['optimistic', Dexie.minKey], ['optimistic', Dexie.maxKey]);
